import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { BRAND } from '../../theme/brandTheme';
import '../../styles/guide-page.css';

/** Scroll to hash anchor with offset for fixed navbar */
export const useGuideHashScroll = () => {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location]);
};

/** Page shell — hero, sticky TOC, content area */
export const GuidePageShell = ({ badge, title, subtitle, toc = [], footerActions = [], children }) => {
  const location = useLocation();
  useGuideHashScroll();

  const activeId = location.hash?.replace('#', '') || toc[0]?.id;

  return (
    <>
      <header className="guide-page-hero">
        <div className="guide-page-hero__inner">
          {badge && <span className="guide-page-hero__badge">{badge}</span>}
          <h1 className="guide-page-hero__title">{title}</h1>
          {subtitle && <p className="guide-page-hero__subtitle">{subtitle}</p>}
        </div>
      </header>

      <div className="guide-page-body">
        <div className="guide-page-layout">
          {toc.length > 0 && (
            <nav className="guide-toc" aria-label="İçindekiler">
              <div className="guide-toc__title">İçindekiler</div>
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`guide-toc__link${activeId === item.id ? ' guide-toc__link--active' : ''}`}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          )}

          <div className="guide-content">
            {toc.length > 0 && (
              <div className="guide-mobile-toc">
                {toc.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="guide-mobile-toc__chip">
                    {item.label}
                  </a>
                ))}
              </div>
            )}
            {children}
            {footerActions.length > 0 && (
              <Box className="guide-footer-actions">
                {footerActions.map((action) => (
                  <Button
                    key={action.to}
                    component={RouterLink}
                    to={action.to}
                    variant={action.variant || 'contained'}
                    startIcon={action.icon}
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      ...(action.variant === 'outlined' && {
                        borderColor: BRAND.purple,
                        color: BRAND.purple,
                      }),
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/** Numbered content section with anchor id */
export const GuideSection = ({ id, number, title, lead, children }) => (
  <section id={id} className="guide-section">
    <div className="guide-section__head">
      {number != null && <span className="guide-section__num">{number}</span>}
      <h2 className="guide-section__title">{title}</h2>
    </div>
    {lead && <p className="guide-section__lead">{lead}</p>}
    {children}
  </section>
);

export const GuideSubtitle = ({ children }) => <h3 className="guide-subtitle">{children}</h3>;

export const GuideFormula = ({ label, formula, description }) => (
  <div className="guide-formula">
    {label && <div className="guide-formula__label">{label}</div>}
    <div className="guide-formula__code">{formula}</div>
    {description && <div className="guide-formula__desc">{description}</div>}
  </div>
);

export const GuideExample = ({ title, children }) => (
  <div className="guide-example">
    {title && <div className="guide-example__title">{title}</div>}
    <div style={{ fontFamily: BRAND.fontFamily, fontSize: 14, lineHeight: 1.65, color: BRAND.textGray }}>
      {children}
    </div>
  </div>
);

export const GuideCallout = ({ variant = 'info', children }) => (
  <div className={`guide-callout guide-callout--${variant}`}>{children}</div>
);

export const GuideMethodCard = ({ title, description, pros = [], cons = [] }) => (
  <div className="guide-method-card">
    <div className="guide-method-card__title">{title}</div>
    <p style={{ fontFamily: BRAND.fontFamily, fontSize: 13, color: BRAND.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
      {description}
    </p>
    {pros.length > 0 && (
      <>
        <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.green, marginBottom: 6 }}>Avantajlar</div>
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, color: BRAND.textGray, lineHeight: 1.6 }}>
          {pros.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </>
    )}
    {cons.length > 0 && (
      <>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#E57702', marginBottom: 6 }}>Dezavantajlar</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: BRAND.textGray, lineHeight: 1.6 }}>
          {cons.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export const GuideTable = ({ columns, rows }) => (
  <div className="guide-table-wrap">
    <table className="guide-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col}>{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const GuideMetricRow = ({ pill, pillColor, children }) => (
  <div className="guide-metric-row">
    <span className="guide-metric-pill" style={pillColor ? { background: pillColor } : undefined}>
      {pill}
    </span>
    <span style={{ fontFamily: BRAND.fontFamily, fontSize: 14, lineHeight: 1.65, color: BRAND.textGray }}>
      {children}
    </span>
  </div>
);

export const GuideFaq = ({ items }) => (
  <div>
    {items.map((item) => (
      <div key={item.q} className="guide-faq-item">
        <div className="guide-faq-item__q">{item.q}</div>
        <p className="guide-faq-item__a">{item.a}</p>
      </div>
    ))}
  </div>
);
